import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useStore } from "@/hooks/useStore";
import { Panel, PageTitle } from "@/components/ui/Panel";
import { Input, Select, Textarea, Label } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { StringList } from "@/components/ui/StringList";
import { uid, nowIso } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import type {
  Card,
  CardType,
  CardStatus,
  AttackDefendFields,
  MechanicEntry,
  CounterRule,
  MechanicAppliesWhen,
  MechanicCondition,
  KeywordSide,
  KeywordSlots,
  KeywordInstanceConfig,
  PerryConfig,
  EffectRow,
  ReactionTrigger,
} from "@/types/card";
import { ALL_REACTION_TRIGGERS } from "@/types/card";
import { EffectListEditor } from "@/components/cards/EffectListEditor";
import { migrateCard } from "@/game/rules/cardMigration";
import { ALL_KEYWORDS, ATTACK_ONLY_KEYWORDS, DEFEND_ONLY_KEYWORDS } from "@/game/rules/keywords";
import type { ReferenceData } from "@/types/reference";

const CARD_TYPES: CardType[] = [
  "Champion",
  "Armor",
  "Equipment",
  "Signature Move",
  "Stance",
  "Utility",
  "Attack/Defend",
  "Token",
];

const STATUSES: CardStatus[] = ["Draft", "Testing", "Finalized"];

function emptyCard(): Card {
  return {
    id: uid("c_"),
    set: "Ashes of The Arena",
    name: "",
    rarity: "Common",
    status: "Draft",
    cardType: "Attack/Defend",
    keywords: [],
    mechanics: [],
    createdAt: nowIso(),
    updatedAt: nowIso(),
    rulesText: "",
    designerNotes: "",
    playtestNotes: "",
    balanceConcerns: "",
    setCardNumber: "",
    attackKeywords: [],
    defendKeywords: [],
  };
}

export function CardForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { state, setState } = useStore();
  const navigate = useNavigate();
  const [card, setCard] = useState<Card>(() => {
    if (id) {
      const found = state.cards.find((c) => c.id === id);
      return found ? migrateLoadedCard(structuredClone(found)) : emptyCard();
    }
    return emptyCard();
  });

  // Sync if store loads after mount
  useEffect(() => {
    if (id && card.name === "" && state.cards.length) {
      const found = state.cards.find((c) => c.id === id);
      if (found) setCard(migrateLoadedCard(structuredClone(found)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.cards.length]);

  const ref = state.referenceData;

  function update<K extends keyof Card>(k: K, v: Card[K]) {
    setCard((c) => ({ ...c, [k]: v }));
  }

  function save() {
    const updated: Card = stripLegacyAttackDefend({
      ...card,
      updatedAt: nowIso(),
    });
    setState((s) => {
      const exists = s.cards.some((c) => c.id === updated.id);
      return {
        ...s,
        cards: exists
          ? s.cards.map((c) => (c.id === updated.id ? updated : c))
          : [...s.cards, updated],
      };
    });
    toast.success(editing ? "Card updated" : "Card created");
    navigate(`/cards/${updated.id}`);
  }

  function remove() {
    if (!confirm("Delete this card?")) return;
    setState((s) => ({ ...s, cards: s.cards.filter((c) => c.id !== card.id) }));
    toast.success("Card deleted");
    navigate("/cards");
  }

  async function onUpload(file: File) {
    try {
      const ext = file.name.split(".").pop() || "png";
      const path = `${card.id}.${ext}`;
      const { error } = await supabase.storage
        .from("card-images")
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      const { data } = supabase.storage.from("card-images").getPublicUrl(path);
      update("imageDataUrl", data.publicUrl);
      update("imageName", file.name);
      toast.success("Image uploaded");
    } catch (e: any) {
      toast.error(`Upload failed: ${e.message ?? e}`);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageTitle>{editing ? "Edit card" : "Add card"}</PageTitle>
        <div className="flex gap-2">
          {editing && (
            <Button variant="danger" onClick={remove}>Delete</Button>
          )}
          <Button onClick={save}>Save</Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_2fr]">
        {/* Image side */}
        <Panel>
          <Label>Image</Label>
          <div className="aspect-[3/4] overflow-hidden rounded bg-[#0f1115]">
            {card.imageDataUrl ? (
              <img src={card.imageDataUrl} alt={card.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-[#64748b]">
                no image
              </div>
            )}
          </div>
          <input
            type="file"
            accept="image/*"
            className="mt-2 text-xs"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
            }}
          />
          <Label className="mt-3">Image filename</Label>
          <Input value={card.imageName ?? ""} onChange={(e) => update("imageName", e.target.value)} />
          <Label className="mt-3">Image URL</Label>
          <Input value={card.imageDataUrl ?? ""} onChange={(e) => update("imageDataUrl", e.target.value)} />
        </Panel>

        {/* Right side: identity + type-specific */}
        <div className="space-y-4">
          <Panel className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>Name</Label>
              <Input value={card.name} onChange={(e) => update("name", e.target.value)} />
            </div>
            <div>
              <Label>Set card number</Label>
              <Input value={card.setCardNumber ?? ""} onChange={(e) => update("setCardNumber", e.target.value)} />
            </div>
            <div>
              <Label>Set</Label>
              <Select value={card.set} onChange={(e) => update("set", e.target.value)}>
                {ref.sets.map((s) => <option key={s}>{s}</option>)}
              </Select>
            </div>
            <div>
              <Label>Rarity</Label>
              <Select value={card.rarity} onChange={(e) => update("rarity", e.target.value)}>
                {ref.rarities.map((r) => <option key={r}>{r}</option>)}
              </Select>
            </div>
            <div>
              <Label>Card type</Label>
              <Select value={card.cardType} onChange={(e) => update("cardType", e.target.value as CardType)}>
                {CARD_TYPES.map((t) => <option key={t}>{t}</option>)}
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={card.status} onChange={(e) => update("status", e.target.value as CardStatus)}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </Select>
            </div>
          </Panel>

          <Panel>
            <Label>Rules text (display text on card)</Label>
            <Textarea rows={4} value={card.rulesText ?? ""} onChange={(e) => update("rulesText", e.target.value)} />
            <div className="mt-3">
              <Label>Mechanic tags (filter only)</Label>
              <StringList value={card.mechanics ?? []} onChange={(v) => update("mechanics", v)} options={ref.mechanics} />
            </div>
          </Panel>

          <TypeFields card={card} setCard={setCard} />

          <KeywordsSection card={card} setCard={setCard} options={ALL_KEYWORDS} />

          <Panel className="space-y-3">
            <div>
              <Label>Designer notes</Label>
              <Textarea rows={2} value={card.designerNotes ?? ""} onChange={(e) => update("designerNotes", e.target.value)} />
            </div>
            <div>
              <Label>Playtest notes</Label>
              <Textarea rows={2} value={card.playtestNotes ?? ""} onChange={(e) => update("playtestNotes", e.target.value)} />
            </div>
            <div>
              <Label>Balance concerns</Label>
              <Textarea rows={2} value={card.balanceConcerns ?? ""} onChange={(e) => update("balanceConcerns", e.target.value)} />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

function TypeFields({
  card,
  setCard,
}: {
  card: Card;
  setCard: React.Dispatch<React.SetStateAction<Card>>;
}) {
  const { state } = useStore();
  const ref = state.referenceData;

  function patch<K extends keyof Card>(k: K, v: Card[K]) {
    setCard((c) => ({ ...c, [k]: v }));
  }

  function patchSub<G extends keyof Card>(
    group: G,
    sub: Partial<NonNullable<Card[G]>>,
  ) {
    setCard((c) => ({ ...c, [group]: { ...(c[group] as object), ...sub } as Card[G] }));
  }

  if (card.cardType === "Champion") {
    const f = card.championFields ?? {};
    return (
      <Panel className="grid gap-3 sm:grid-cols-2">
        <div><Label>Speed</Label><Input value={f.speed ?? ""} onChange={(e) => patchSub("championFields", { speed: e.target.value })} /></div>
        <div><Label>Equipment stat</Label><Input value={f.equipmentStat ?? ""} onChange={(e) => patchSub("championFields", { equipmentStat: e.target.value })} /></div>
        <div>
          <Label>Faction</Label>
          <Select value={f.faction ?? ""} onChange={(e) => patchSub("championFields", { faction: e.target.value })}>
            <option value="">—</option>
            {ref.factions.map((x) => <option key={x}>{x}</option>)}
          </Select>
        </div>
        <div>
          <Label>Champion type</Label>
          <StringList value={f.championType ?? []} onChange={(v) => patchSub("championFields", { championType: v })} options={ref.championTypes} />
        </div>
        <div className="sm:col-span-2"><Label>Ability</Label><Textarea rows={4} value={f.ability ?? ""} onChange={(e) => patchSub("championFields", { ability: e.target.value })} /></div>
      </Panel>
    );
  }

  if (card.cardType === "Armor") {
    const f = card.armorFields ?? {};
    return (
      <Panel className="grid gap-3 sm:grid-cols-2">
        <div><Label>Weight</Label><Input value={f.weight ?? ""} onChange={(e) => patchSub("armorFields", { weight: e.target.value })} /></div>
        <div><Label>Block value</Label><Input value={f.blockValue ?? ""} onChange={(e) => patchSub("armorFields", { blockValue: e.target.value })} /></div>
        <div className="sm:col-span-2"><Label>Ability</Label><Textarea rows={4} value={f.ability ?? ""} onChange={(e) => patchSub("armorFields", { ability: e.target.value })} /></div>
      </Panel>
    );
  }

  if (card.cardType === "Equipment") {
    const f = card.equipmentFields ?? {};
    return (
      <Panel className="grid gap-3 sm:grid-cols-3">
        <div>
          <Label>Equipment subtype</Label>
          <Select value={f.equipmentSubtype ?? ""} onChange={(e) => patchSub("equipmentFields", { equipmentSubtype: e.target.value })}>
            <option value="">—</option>
            {ref.equipmentSubtypes.map((x) => <option key={x}>{x}</option>)}
          </Select>
        </div>
        <div>
          <Label>Secondary subtype</Label>
          <Input value={f.secondarySubtype ?? ""} onChange={(e) => patchSub("equipmentFields", { secondarySubtype: e.target.value })} />
        </div>
        <div><Label>Equipment value</Label><Input value={f.equipmentValue ?? ""} onChange={(e) => patchSub("equipmentFields", { equipmentValue: e.target.value })} /></div>
        <div><Label>Attack type</Label><Input value={f.attackType ?? ""} onChange={(e) => patchSub("equipmentFields", { attackType: e.target.value })} /></div>
        <div><Label>Attack weight</Label><Input value={f.attackWeight ?? ""} onChange={(e) => patchSub("equipmentFields", { attackWeight: e.target.value })} /></div>
        <div><Label>Power</Label><Input value={f.powerValue ?? ""} onChange={(e) => patchSub("equipmentFields", { powerValue: e.target.value })} /></div>
        <div><Label>Defend type</Label><Input value={f.defendType ?? ""} onChange={(e) => patchSub("equipmentFields", { defendType: e.target.value })} /></div>
        <div><Label>Defend weight</Label><Input value={f.defendWeight ?? ""} onChange={(e) => patchSub("equipmentFields", { defendWeight: e.target.value })} /></div>
        <div><Label>Block</Label><Input value={f.blockValue ?? ""} onChange={(e) => patchSub("equipmentFields", { blockValue: e.target.value })} /></div>
        <div><Label>Stamina cost</Label><Input value={f.staminaCost ?? ""} onChange={(e) => patchSub("equipmentFields", { staminaCost: e.target.value })} /></div>
        <div className="sm:col-span-3"><Label>Ability</Label><Textarea rows={3} value={f.ability ?? ""} onChange={(e) => patchSub("equipmentFields", { ability: e.target.value })} /></div>
      </Panel>
    );
  }

  if (card.cardType === "Signature Move") {
    const f = card.signatureMoveFields ?? {};
    return (
      <Panel className="grid gap-3 sm:grid-cols-3">
        <div className="sm:col-span-3"><Label>Condition</Label><Input value={f.condition ?? ""} onChange={(e) => patchSub("signatureMoveFields", { condition: e.target.value })} /></div>
        <div><Label>Attack type</Label><Input value={f.attackType ?? ""} onChange={(e) => patchSub("signatureMoveFields", { attackType: e.target.value })} /></div>
        <div><Label>Attack weight</Label><Input value={f.attackWeight ?? ""} onChange={(e) => patchSub("signatureMoveFields", { attackWeight: e.target.value })} /></div>
        <div><Label>Power</Label><Input value={f.powerValue ?? ""} onChange={(e) => patchSub("signatureMoveFields", { powerValue: e.target.value })} /></div>
        <div><Label>Block</Label><Input value={f.blockValue ?? ""} onChange={(e) => patchSub("signatureMoveFields", { blockValue: e.target.value })} /></div>
        <div><Label>Stamina cost</Label><Input value={f.staminaCost ?? ""} onChange={(e) => patchSub("signatureMoveFields", { staminaCost: e.target.value })} /></div>
        <div className="sm:col-span-3"><Label>Ability</Label><Textarea rows={3} value={f.ability ?? ""} onChange={(e) => patchSub("signatureMoveFields", { ability: e.target.value })} /></div>
      </Panel>
    );
  }

  if (card.cardType === "Stance") {
    const f = card.stanceFields ?? {};
    return (
      <Panel className="grid gap-3">
        <div><Label>Type requirement</Label><StringList value={f.typeRequirement ?? []} onChange={(v) => patchSub("stanceFields", { typeRequirement: v })} options={ref.championTypes} /></div>
        <div><Label>Ability</Label><Textarea rows={3} value={f.ability ?? ""} onChange={(e) => patchSub("stanceFields", { ability: e.target.value })} /></div>
      </Panel>
    );
  }

  if (card.cardType === "Utility") {
    const f = card.utilityFields ?? {};
    const isReaction = (f.utilityType ?? "").toLowerCase() === "reaction";
    const selectedTriggers = new Set<ReactionTrigger>(f.reactionTriggers ?? []);
    const toggleTrigger = (t: ReactionTrigger) => {
      const next = new Set(selectedTriggers);
      if (next.has(t)) next.delete(t);
      else next.add(t);
      patchSub("utilityFields", { reactionTriggers: Array.from(next) });
    };
    return (
      <Panel className="grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Utility type</Label>
          <Select
            value={f.utilityType ?? ""}
            onChange={(e) => {
              const newType = e.target.value;
              // Clear reaction triggers when switching away from Reaction subtype
              const patch: Partial<typeof f> = { utilityType: newType };
              if (newType.toLowerCase() !== "reaction") patch.reactionTriggers = [];
              patchSub("utilityFields", patch);
            }}
          >
            <option value="">—</option>
            {ref.utilityTypes.map((x) => <option key={x}>{x}</option>)}
          </Select>
        </div>
        <div><Label>Type requirement</Label><StringList value={f.typeRequirement ?? []} onChange={(v) => patchSub("utilityFields", { typeRequirement: v })} options={ref.championTypes} /></div>
        <div><Label>Stamina cost</Label><Input value={f.staminaCost ?? ""} onChange={(e) => patchSub("utilityFields", { staminaCost: e.target.value })} placeholder="0" /></div>
        <div className="sm:col-span-2"><Label>Ability</Label><Textarea rows={3} value={f.ability ?? ""} onChange={(e) => patchSub("utilityFields", { ability: e.target.value })} /></div>
        {isReaction && (
          <div className="sm:col-span-2">
            <Label>Reaction triggers</Label>
            <div className="text-xs text-zinc-400 mb-2">
              When any of the selected events happen, this card becomes a legal play in the opponent's Action Chain reaction window.
            </div>
            <div className="grid gap-1 sm:grid-cols-2">
              {ALL_REACTION_TRIGGERS.map((trig) => (
                <label key={trig} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selectedTriggers.has(trig)}
                    onChange={() => toggleTrigger(trig)}
                  />
                  <span className="font-mono text-xs">{trig}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </Panel>
    );
  }

  if (card.cardType === "Token") {
    const f = card.tokenFields ?? {};
    return (
      <Panel className="grid gap-3 sm:grid-cols-3">
        <div><Label>Stamina cost</Label><Input value={f.staminaCost ?? ""} onChange={(e) => patchSub("tokenFields", { staminaCost: e.target.value })} /></div>
        <div><Label>Power</Label><Input value={f.powerValue ?? ""} onChange={(e) => patchSub("tokenFields", { powerValue: e.target.value })} /></div>
        <div><Label>Block</Label><Input value={f.blockValue ?? ""} onChange={(e) => patchSub("tokenFields", { blockValue: e.target.value })} /></div>
        <div className="sm:col-span-3"><Label>Ability</Label><Textarea rows={3} value={f.ability ?? ""} onChange={(e) => patchSub("tokenFields", { ability: e.target.value })} /></div>
      </Panel>
    );
  }

  // Attack/Defend
  const f = card.attackDefendFields ?? ({} as AttackDefendFields);
  return (
    <Panel className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>Weight</Label><Input value={f.weight ?? ""} onChange={(e) => patchSub("attackDefendFields", { weight: e.target.value })} /></div>
        <div><Label>Stamina cost</Label><Input value={f.staminaCost ?? ""} onChange={(e) => patchSub("attackDefendFields", { staminaCost: e.target.value })} /></div>
        <div><Label>Type requirement</Label><StringList value={f.typeRequirement ?? []} onChange={(v) => patchSub("attackDefendFields", { typeRequirement: v })} options={ref.championTypes} /></div>
        <div><Label>Attack type</Label><Input value={f.attackType ?? ""} onChange={(e) => patchSub("attackDefendFields", { attackType: e.target.value })} /></div>
        <div><Label>Defend type</Label><Input value={f.defendType ?? ""} onChange={(e) => patchSub("attackDefendFields", { defendType: e.target.value })} /></div>
        <label className="flex items-end gap-2 text-sm">
          <input type="checkbox" checked={Boolean(f.isSpecialMove)} onChange={(e) => patchSub("attackDefendFields", { isSpecialMove: e.target.checked })} />
          Special Move
        </label>
        <div><Label>Power</Label><Input value={f.powerValue ?? ""} onChange={(e) => patchSub("attackDefendFields", { powerValue: e.target.value })} /></div>
        <div><Label>Block</Label><Input value={f.blockValue ?? ""} onChange={(e) => patchSub("attackDefendFields", { blockValue: e.target.value })} /></div>
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div><Label>Attack ability</Label><Textarea rows={3} value={f.attackAbility ?? ""} onChange={(e) => patchSub("attackDefendFields", { attackAbility: e.target.value })} /></div>
        <div><Label>Defend ability</Label><Textarea rows={3} value={f.defendAbility ?? ""} onChange={(e) => patchSub("attackDefendFields", { defendAbility: e.target.value })} /></div>
      </div>

      <div className="border-t border-[#2a2f3a] pt-3">
        <h4 className="font-display text-sm font-semibold text-amber-soft">Counter</h4>
        <p className="mb-2 text-xs text-[#64748b]">
          Each rule says <em>when</em> the counter activates and <em>what</em> happens.
          Effects buff your next action or debuff the opponent's. Multiple effects allowed.
        </p>
        <div className="mb-2">
          <Label>Counter ability text (flavor / display)</Label>
          <Textarea rows={2} value={f.counterAbility ?? ""} onChange={(e) => patchSub("attackDefendFields", { counterAbility: e.target.value })} />
        </div>
        <CounterRulesEditor
          value={f.counterRules ?? []}
          onChange={(v) => patchSub("attackDefendFields", { counterRules: v })}
          referenceData={ref}
        />
      </div>

      <div className="border-t border-[#2a2f3a] pt-3">
        <h4 className="font-display text-sm font-semibold text-amber-soft">Mechanics</h4>
        <p className="mb-2 text-xs text-[#64748b]">
          Triggered abilities that fire while this card is in active combat. Pick when it applies
          (attack/defend), the condition that gates it, and the effects to apply.
        </p>
        <MechanicsEditor value={f.mechanics ?? []} onChange={(v) => patchSub("attackDefendFields", { mechanics: v })} />
      </div>
    </Panel>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const WEIGHT_OPTIONS = ["Light", "Medium", "Heavy"];

function migrateLoadedCard(c: Card): Card {
  return migrateCard(c);
}

function cardHasKeyword(c: Card, kw: string): boolean {
  const lc = kw.toLowerCase();
  const lists = [c.keywords, c.attackKeywords, c.defendKeywords];
  return lists.some((list) =>
    Array.isArray(list) && list.some((k) => (k ?? "").toLowerCase() === lc),
  );
}

/** On save, drop legacy fields the new editor no longer writes. The new
 *  schema (counterRules + structured MechanicEntry) is the source of truth. */
function stripLegacyAttackDefend(c: Card): Card {
  if (!c.attackDefendFields) return c;
  const ad = c.attackDefendFields;
  const cleaned: AttackDefendFields = { ...ad };
  delete cleaned.counterScope;
  delete cleaned.counterGrants;
  delete cleaned.counterRequirement;
  delete cleaned.counterConditionType;
  delete cleaned.counterConditionValue;
  if (Array.isArray(cleaned.mechanics)) {
    cleaned.mechanics = cleaned.mechanics.map((m) => ({
      appliesWhen: m.appliesWhen,
      condition: m.condition,
      effects: m.effects ?? [],
    }));
  }
  return { ...c, attackDefendFields: cleaned };
}

// ─── Keywords section (unified attack + defend with per-slot config) ──────

  function KeywordsSection({
  card,
  setCard,
  options,
}: {
  card: Card;
  setCard: React.Dispatch<React.SetStateAction<Card>>;
  options: string[];
}) {
  // The displayed keyword chips for each slot come from attackKeywords / defendKeywords.
  // The config for each chip lives in keywordSlots[side][lc].
  // Utility-style keywords live in top-level card.keywords.
  const isUtility = card.cardType === "Utility";
  const isSignatureMove = card.cardType === "Signature Move";

  function setSlotList(side: KeywordSide, list: string[]) {
    setCard((c) => {
      const slots: KeywordSlots = {
        attack: { ...(c.keywordSlots?.attack ?? {}) },
        defend: { ...(c.keywordSlots?.defend ?? {}) },
      };
      const sideMap = { ...(slots[side] ?? {}) };
      const keep = new Set(list.map((s) => s.toLowerCase()));

      for (const lc of Object.keys(sideMap)) {
        if (!keep.has(lc)) delete sideMap[lc];
      }

      for (const kw of list) {
        const lc = kw.toLowerCase();
        if (!sideMap[lc]) sideMap[lc] = {};
      }

      slots[side] = sideMap;

      const next: Card = { ...c, keywordSlots: slots };
      if (side === "attack") next.attackKeywords = list;
      else next.defendKeywords = list;
      return next;
    });
  }

  function setSlotConfig(
    side: KeywordSide,
    keywordLc: string,
    nextCfg: KeywordInstanceConfig,
  ) {
    setCard((c) => {
      const slots: KeywordSlots = {
        attack: { ...(c.keywordSlots?.attack ?? {}) },
        defend: { ...(c.keywordSlots?.defend ?? {}) },
      };
      const sideMap = { ...(slots[side] ?? {}) };
      sideMap[keywordLc] = nextCfg;
      slots[side] = sideMap;
      return { ...c, keywordSlots: slots };
    });
  }

  function setUtilityKeywords(list: string[]) {
    setCard((c) => ({ ...c, keywords: list }));
  }

  if (isUtility) {
    return (
      <Panel className="space-y-4">
        <div>
          <h3 className="font-display text-sm font-semibold text-amber-soft">Keywords</h3>
          <p className="text-xs text-64748b">
            Utility cards use utility-side keywords like Negate. Attack and defend keywords
            don&apos;t apply because utilities aren&apos;t played as attacks or defends.
          </p>
        </div>

        <div className="space-y-2">
          <Label>Utility keywords</Label>
          <StringList
            value={card.keywords ?? []}
            onChange={setUtilityKeywords}
            options={options.filter(
              (k) => !ATTACK_ONLY_KEYWORDS.has(k) && !DEFEND_ONLY_KEYWORDS.has(k),
            )}
          />
          <p className="text-11px text-64748b">
            Negate: when this utility resolves in response to an opponent&apos;s stack entry,
            pop that entry, refund its stamina, and reverse any state changes from playing it.
          </p>
        </div>
      </Panel>
    );
  }

  return (
    <Panel className="space-y-4">
      <div>
        <h3 className="font-display text-sm font-semibold text-amber-soft">Keywords</h3>
        <p className="text-xs text-64748b">
          Add keywords to the attack and defend sides of this card. Keywords with parameters
          (Reflect, Disarm, Perry) expose their settings inline.
        </p>
      </div>

      {isSignatureMove ? (
        <div className="space-y-2 border-t border-2a2f3a pt-3 first:border-t-0 first:pt-0">
          <Label>Utility keywords</Label>
          <StringList
            value={card.keywords ?? []}
            onChange={setUtilityKeywords}
            options={options.filter(
              (k) => !ATTACK_ONLY_KEYWORDS.has(k) && !DEFEND_ONLY_KEYWORDS.has(k),
            )}
          />
          <p className="text-11px text-64748b">
            These apply when the signature move is played as a utility, such as Negate for
            Reversal.
          </p>
        </div>
      ) : null}

      <KeywordSlotEditor
        title="Attack keywords"
        side="attack"
        list={card.attackKeywords ?? []}
        slotMap={card.keywordSlots?.attack ?? {}}
        options={options.filter((k) => !DEFEND_ONLY_KEYWORDS.has(k))}
        onListChange={(v) => setSlotList("attack", v)}
        onConfigChange={(lc, cfg) => setSlotConfig("attack", lc, cfg)}
      />

      <KeywordSlotEditor
        title="Defend keywords"
        side="defend"
        list={card.defendKeywords ?? []}
        slotMap={card.keywordSlots?.defend ?? {}}
        options={options.filter((k) => !ATTACK_ONLY_KEYWORDS.has(k))}
        onListChange={(v) => setSlotList("defend", v)}
        onConfigChange={(lc, cfg) => setSlotConfig("defend", lc, cfg)}
      />
    </Panel>
  );
}

function KeywordSlotEditor({
  title,
  side,
  list,
  slotMap,
  options,
  onListChange,
  onConfigChange,
}: {
  title: string;
  side: KeywordSide;
  list: string[];
  slotMap: Record<string, KeywordInstanceConfig>;
  options: string[];
  onListChange: (v: string[]) => void;
  onConfigChange: (keywordLc: string, cfg: KeywordInstanceConfig) => void;
}) {
  return (
    <div className="space-y-2 border-t border-[#2a2f3a] pt-3 first:border-t-0 first:pt-0">
      <Label>{title}</Label>
      <StringList value={list} onChange={onListChange} options={options} />

      {list.length > 0 && (
        <div className="space-y-2 pl-1">
          {list.map((kw) => {
            const lc = kw.toLowerCase();
            const cfg = slotMap[lc] ?? {};
            const config = needsConfig(kw, side);
            if (!config) return null;
            return (
              <div
                key={`${side}-${lc}`}
                className="rounded border border-[#2a2f3a] bg-[#0f1115] p-3"
              >
                <div className="mb-2 text-xs font-semibold text-amber-soft">
                  {kw}
                </div>
                <KeywordInstanceEditor
                  keyword={kw}
                  side={side}
                  value={cfg}
                  onChange={(next) => onConfigChange(lc, next)}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** True when this keyword on this side has any parameters to configure. */
function needsConfig(keyword: string, side: KeywordSide): boolean {
  const lc = keyword.toLowerCase();
  if (lc === "reflect" && side === "defend") return true;
  if (lc === "disarm" && side === "attack") return true;
  if (lc === "perry" && side === "defend") return true;
  return false;
}

function KeywordInstanceEditor({
  keyword,
  side,
  value,
  onChange,
}: {
  keyword: string;
  side: KeywordSide;
  value: KeywordInstanceConfig;
  onChange: (next: KeywordInstanceConfig) => void;
}) {
  const lc = keyword.toLowerCase();

  if (lc === "reflect" && side === "defend") {
    return (
      <div className="grid gap-2 sm:grid-cols-[160px_1fr] items-end">
        <div>
          <Label>Reflect amount (X)</Label>
          <Input
            type="number"
            min={0}
            value={value.reflect ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                const { reflect, ...rest } = value;
                onChange(rest);
              } else {
                const n = Number(raw);
                onChange({ ...value, reflect: Number.isFinite(n) ? n : 0 });
              }
            }}
          />
        </div>
        <p className="text-xs text-[#64748b]">
          When this defend blocks an attack, reflect X damage back to the attacker.
        </p>
      </div>
    );
  }

  if (lc === "disarm" && side === "attack") {
    return (
      <div className="grid gap-2 sm:grid-cols-[160px_1fr] items-end">
        <div>
          <Label>Disarm count (X)</Label>
          <Input
            type="number"
            min={0}
            value={value.disarm ?? ""}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === "") {
                const { disarm, ...rest } = value;
                onChange(rest);
              } else {
                const n = Number(raw);
                onChange({ ...value, disarm: Number.isFinite(n) ? n : 0 });
              }
            }}
          />
        </div>
        <p className="text-xs text-[#64748b]">
          When this attack lands, disarm X items (untapped weapons first, then disarm tokens).
        </p>
      </div>
    );
  }

  if (lc === "perry" && side === "defend") {
    const perry: PerryConfig = value.perry ?? { appliesTo: "attack", effects: [] };
    function patchPerry(next: Partial<PerryConfig>) {
      onChange({ ...value, perry: { ...perry, ...next } });
    }
    return (
      <div className="space-y-2">
        <div className="grid gap-2 sm:grid-cols-[200px_1fr] items-end">
          <div>
            <Label>Perry applies to</Label>
            <Select
              value={perry.appliesTo}
              onChange={(e) =>
                patchPerry({
                  appliesTo: e.target.value as PerryConfig["appliesTo"],
                })
              }
            >
              <option value="attack">My next attack</option>
              <option value="defend">My next defend</option>
              <option value="action">My next action (any)</option>
            </Select>
          </div>
          <p className="text-xs text-[#64748b]">
            When this defend full-blocks an attack, the effects below land on
            your chosen next action.
          </p>
        </div>
        <div>
          <Label>Perry effects</Label>
          <EffectListEditor
            value={perry.effects}
            onChange={(rows: EffectRow[]) => patchPerry({ effects: rows })}
            allowPopupTargets={false}
            allowNextTargets={true}
            hint="Effects fire on the chosen next action. Buff yourself or debuff the opponent."
          />
        </div>
      </div>
    );
  }

  return null;
}

// ─── Counter rules editor ──────────────────────────────────────────────────

function CounterRulesEditor({
  value,
  onChange,
  referenceData,
}: {
  value: CounterRule[];
  onChange: (v: CounterRule[]) => void;
  referenceData: ReferenceData;
}) {
  function add() {
    onChange([
      ...value,
      {
        trigger: {
          actionPlayed: "either",
          weights: [],
          attackTypes: [],
          defendTypes: [],
          hasKeywords: [],
        },
        effects: [
          {
            direction: "buff-self",
            target: "my-next-attack",
            effect: "power",
            amount: 1,
          },
        ],
      },
    ]);
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function patchTrigger(i: number, p: Partial<CounterRule["trigger"]>) {
    onChange(
      value.map((r, idx) =>
        idx === i ? { ...r, trigger: { ...r.trigger, ...p } } : r,
      ),
    );
  }
  function patchEffects(i: number, effects: CounterRule["effects"]) {
    onChange(value.map((r, idx) => (idx === i ? { ...r, effects } : r)));
  }

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <p className="text-xs italic text-[#475569]">No counter rules yet.</p>
      ) : null}
      {value.map((rule, i) => (
        <div
          key={i}
          className="space-y-3 rounded border border-amber/20 bg-[#0f1115] p-3"
        >
          <div className="flex items-center justify-between">
            <span className="font-display text-xs uppercase tracking-wide text-amber-soft">
              Rule {i + 1}
            </span>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}>
              Remove rule
            </Button>
          </div>

          <div>
            <h5 className="mb-1 text-xs font-semibold text-[#94a3b8]">
              Activates when opponent plays…
            </h5>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Action played</Label>
                <Select
                  value={rule.trigger.actionPlayed}
                  onChange={(e) =>
                    patchTrigger(i, {
                      actionPlayed: e.target.value as
                        | "attack"
                        | "defend"
                        | "either",
                    })
                  }
                >
                  <option value="either">attack or defend</option>
                  <option value="attack">attack only</option>
                  <option value="defend">defend only</option>
                </Select>
              </div>
              <div>
                <Label>Weights (any of)</Label>
                <StringList
                  value={rule.trigger.weights}
                  onChange={(v) => patchTrigger(i, { weights: v })}
                  options={WEIGHT_OPTIONS}
                  placeholder="Light / Medium / Heavy"
                />
              </div>
              {rule.trigger.actionPlayed !== "defend" ? (
                <div>
                  <Label>Attack types (any of)</Label>
                  <StringList
                    value={rule.trigger.attackTypes}
                    onChange={(v) => patchTrigger(i, { attackTypes: v })}
                    placeholder="e.g. Slash"
                  />
                </div>
              ) : null}
              {rule.trigger.actionPlayed !== "attack" ? (
                <div>
                  <Label>Defend types (any of)</Label>
                  <StringList
                    value={rule.trigger.defendTypes}
                    onChange={(v) => patchTrigger(i, { defendTypes: v })}
                    placeholder="e.g. Block"
                  />
                </div>
              ) : null}
              <div className="sm:col-span-2">
                <Label>Has keywords (any of)</Label>
                <StringList
                  value={rule.trigger.hasKeywords}
                  onChange={(v) => patchTrigger(i, { hasKeywords: v })}
                  options={ALL_KEYWORDS}
                  placeholder="Stun, Quick…"
                />
              </div>
              <div className="sm:col-span-2">
                <Label title="Match opponent's played card by its SOURCE (where the attack/defend came from). attack-defend = standard A/D card, equipment = a weapon's printed attack/defend, signature = a Signature Move played as attack/defend. Empty = match any source. Used by No Time (cardKinds=[equipment] → silence weapon attack keywords).">
                  Card kinds (any of)
                </Label>
                <StringList
                  value={rule.trigger.cardKinds ?? []}
                  onChange={(v) =>
                    patchTrigger(i, {
                      cardKinds: v.length
                        ? (v as Array<"attack-defend" | "equipment" | "signature">)
                        : undefined,
                    })
                  }
                  options={["attack-defend", "equipment", "signature"]}
                  placeholder="attack-defend / equipment / signature"
                />
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input
                  id={`opp-drew-${i}`}
                  type="checkbox"
                  checked={!!rule.trigger.opponentDrewThisRound}
                  onChange={(e) =>
                    patchTrigger(i, {
                      opponentDrewThisRound: e.target.checked || undefined,
                    })
                  }
                />
                <Label htmlFor={`opp-drew-${i}`} className="!mb-0">
                  Opponent drew a card this round (event trigger)
                </Label>
              </div>
              {/* Phase 3 event-driven gates */}
              <div className="sm:col-span-2 flex items-center gap-2">
                <input
                  id={`self-stunned-${i}`}
                  type="checkbox"
                  checked={!!rule.trigger.selfStunnedThisRound}
                  onChange={(e) =>
                    patchTrigger(i, {
                      selfStunnedThisRound: e.target.checked || undefined,
                    })
                  }
                />
                <Label htmlFor={`self-stunned-${i}`} className="!mb-0">
                  You were stunned this round (event trigger — used by Crimson Tempest)
                </Label>
              </div>
              <div className="sm:col-span-2 flex items-center gap-2">
                <input
                  id={`self-took-damage-${i}`}
                  type="checkbox"
                  checked={!!rule.trigger.selfTookDamageThisRound}
                  onChange={(e) =>
                    patchTrigger(i, {
                      selfTookDamageThisRound: e.target.checked || undefined,
                    })
                  }
                />
                <Label htmlFor={`self-took-damage-${i}`} className="!mb-0">
                  You took damage this round (event trigger — used by Calculate Risk)
                </Label>
              </div>
              {rule.trigger.actionPlayed !== "defend" ? (
                <div>
                  <Label title="Match when opponent's live attack power (printed + buffs) is at least this value. Leave 0 for no gate.">
                    Min buffed power (attack)
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={rule.trigger.minBuffedPower ?? 0}
                    onChange={(e) =>
                      patchTrigger(i, {
                        minBuffedPower: Math.max(0, Number(e.target.value) || 0) || undefined,
                      })
                    }
                  />
                </div>
              ) : null}
              {rule.trigger.actionPlayed !== "attack" ? (
                <div>
                  <Label title="Match when opponent's live block (printed + buffs) is at least this value. Leave 0 for no gate.">
                    Min buffed block (defend)
                  </Label>
                  <Input
                    type="number"
                    min={0}
                    value={rule.trigger.minBuffedBlock ?? 0}
                    onChange={(e) =>
                      patchTrigger(i, {
                        minBuffedBlock: Math.max(0, Number(e.target.value) || 0) || undefined,
                      })
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>

          <div>
            <h5 className="mb-1 text-xs font-semibold text-[#94a3b8]">
              Effects when this rule fires
            </h5>
            <EffectListEditor
              value={rule.effects}
              onChange={(v) => patchEffects(i, v)}
              allowNextTargets={true}
              allowPopupTargets={true}
              hint={"Counters fire mid-combat. Use popup-* to edit the live action on the table (e.g. ‘strip queued power buffs from popup-attack’ like Hardened Hands). Use my-next-* to queue onto your next play. Min buffed power/block gates above are great for cards that punish a buffed-up swing."}
            />
          </div>
        </div>
      ))}
      <Button variant="secondary" type="button" onClick={add}>
        + Add counter rule
      </Button>
    </div>
  );
}

// ─── Mechanics editor ──────────────────────────────────────────────────────

function MechanicsEditor({
  value,
  onChange,
}: {
  value: MechanicEntry[];
  onChange: (v: MechanicEntry[]) => void;
}) {
  function add() {
    onChange([
      ...value,
      {
        appliesWhen: "attack",
        condition: "none",
        effects: [
          {
            direction: "buff-self",
            target: "my-next-attack",
            effect: "power",
            amount: 1,
          },
        ],
      },
    ]);
  }
  function remove(i: number) {
    onChange(value.filter((_, idx) => idx !== i));
  }
  function patch(i: number, p: Partial<MechanicEntry>) {
    onChange(value.map((m, idx) => (idx === i ? { ...m, ...p } : m)));
  }
  function patchEffects(i: number, effects: MechanicEntry["effects"]) {
    onChange(value.map((m, idx) => (idx === i ? { ...m, effects } : m)));
  }

  function conditionsForApplies(
    a: MechanicAppliesWhen,
  ): { value: MechanicCondition; label: string }[] {
    const all: { value: MechanicCondition; label: string }[] = [
      { value: "none", label: "No condition — fires on play (e.g. queue +power on next attack)" },
      { value: "attack-deals-damage", label: "When this attack deals damage (resolve-time, e.g. gain stamina)" },
      { value: "attack-fully-blocked", label: "If this attack is fully blocked (resolve-time payback)" },
      { value: "block-fully-blocks", label: "If this block fully blocks the attack (resolve-time payoff)" },
    ];
    return all.filter((c) => {
      if (a === "defend") {
        return c.value === "none" || c.value === "block-fully-blocks";
      }
      if (a === "attack") {
        return (
          c.value === "none" ||
          c.value === "attack-deals-damage" ||
          c.value === "attack-fully-blocked"
        );
      }
      return true;
    });
  }

  return (
    <div className="space-y-3">
      {value.length === 0 ? (
        <p className="text-xs italic text-[#475569]">No mechanics yet.</p>
      ) : null}
      {value.map((m, i) => {
        const conds = conditionsForApplies(m.appliesWhen);
        const condValid = conds.some((c) => c.value === m.condition);
        return (
          <div
            key={i}
            className="space-y-3 rounded border border-amber/20 bg-[#0f1115] p-3"
          >
            <div className="flex items-center justify-between">
              <span className="font-display text-xs uppercase tracking-wide text-amber-soft">
                Mechanic {i + 1}
              </span>
              <Button variant="ghost" size="sm" onClick={() => remove(i)}>
                Remove
              </Button>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Applies when this card is played as</Label>
                <Select
                  value={m.appliesWhen}
                  onChange={(e) => {
                    const next = e.target.value as MechanicAppliesWhen;
                    const allowed = conditionsForApplies(next).map(
                      (c) => c.value,
                    );
                    const condition: MechanicCondition = allowed.includes(
                      m.condition,
                    )
                      ? m.condition
                      : "none";
                    patch(i, { appliesWhen: next, condition });
                  }}
                >
                  <option value="attack">an attack</option>
                  <option value="defend">a defend</option>
                  <option value="either">attack or defend</option>
                </Select>
              </div>
              <div>
                <Label>Condition</Label>
                <Select
                  value={condValid ? m.condition : "none"}
                  onChange={(e) =>
                    patch(i, {
                      condition: e.target.value as MechanicCondition,
                    })
                  }
                >
                  {conds.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <div>
              <h5 className="mb-1 text-xs font-semibold text-[#94a3b8]">
                Effects when this mechanic fires
              </h5>
              <EffectListEditor
                value={m.effects ?? []}
                onChange={(v) => patchEffects(i, v)}
                allowNextTargets={true}
                allowThisTargets={m.condition === "none"}
                allowPopupTargets={false}
                hint={
                  m.condition === "none"
                    ? "Fires as the card is played. Common patterns: ‘+1 power/block on my next attack/defend’ (queued buff), ‘-1 stamina cost on my next action’ (stacks across cards), ‘add Quick to this attack’ (this-* target)."
                    : "Fires after combat resolves — effects queue onto your NEXT action. Common: ‘gain N stamina when this attack deals damage’, ‘+power on my next attack if this fully blocks’."
                }
              />
            </div>
          </div>
        );
      })}
      <Button variant="secondary" type="button" onClick={add}>
        + Add mechanic
      </Button>
    </div>
  );
}
